import { c as _c } from "react/compiler-runtime";
import { shuffle } from "es-toolkit/array";
import { createContext, useContext, useState } from "react";
import { create, useStore } from "zustand";
import { mutative } from "zustand-mutative";
import { TRANSITION_DURATION } from "./hooks";
export const CONSTELLATION_BACKGROUND_ROOT_ID = "root";
function updateBackgroundVisibility(background, visible) {
  if (background.visible === visible) {
    return background;
  }
  if (visible) {
    return {
      ...background,
      visible
    };
  }
  return {
    ...background,
    visible,
    poppedAt: Date.now()
  };
}
const NON_ROOT_TRANSITION_DURATION_MS = TRANSITION_DURATION / 2 * 1000;
function determineVisibleBackgrounds(backgrounds) {
  return backgrounds.filter(background => {
    // Never remove visible or root background
    if (background.id === CONSTELLATION_BACKGROUND_ROOT_ID || background.visible) {
      return true;
    }

    // Check if background should be removed from memory by checking if the animation is complete
    // Get the time since the background has been popped (Date.now() - bg.poppedAt) ...
    // ... and check if it is more than the transition duration for non-root backgrounds (*1000 to turn seconds into ms)

    const timeSincePopped = Date.now() - background.poppedAt;
    return NON_ROOT_TRANSITION_DURATION_MS > timeSincePopped;
  });
}
const createConstellationBackgroundStore = () => create()(mutative((set, get) => ({
  open: false,
  setOpen: open => {
    set({
      open
    });
  },
  backgrounds: [],
  pushBackground: background => {
    background.items = shuffle(background.items);
    set(state => {
      // Make all backgrounds invisible
      state.backgrounds = state.backgrounds.map(_background => updateBackgroundVisibility(_background, false));

      // Remove backgrounds that are stale
      state.backgrounds = determineVisibleBackgrounds(state.backgrounds);

      // Remove backgrounds that match the one we are trying to push
      state.backgrounds = state.backgrounds.filter(_background => _background.id !== background.id);

      // Push new background
      state.backgrounds.push({
        ...background,
        visible: true
      });
    });
  },
  pushRootBackground: background => {
    get().pushBackground({
      ...background,
      id: CONSTELLATION_BACKGROUND_ROOT_ID
    });
  },
  setBackgroundVisibility: (backgroundId, visible, defocus = false) => {
    set(state => {
      if (!state.hasBackgroundWithId(backgroundId)) {
        return;
      }

      // Remove backgrounds that are stale
      state.backgrounds = determineVisibleBackgrounds(state.backgrounds);

      // Make background id specified visible
      state.backgrounds = state.backgrounds.map(background => ({
        ...updateBackgroundVisibility(background, background.id === backgroundId ? visible : false),
        defocus
      }));
    });
  },
  setBackgroundAnimationOptions: (backgroundId, options) => {
    set(state => {
      if (!state.hasBackgroundWithId(backgroundId)) {
        return;
      }

      // Remove backgrounds that are stale
      state.backgrounds = determineVisibleBackgrounds(state.backgrounds);

      // Update background options
      state.backgrounds = state.backgrounds.map(background => ({
        ...background,
        options: background.id === backgroundId ? options : background.options
      }));
    });
  },
  popToRootBackground: () => {
    get().setBackgroundVisibility(CONSTELLATION_BACKGROUND_ROOT_ID, true);
  },
  hasBackgroundWithId: id => {
    return Boolean(get().backgrounds.find(background => id === background.id));
  },
  getTopLevelBackgroundId: backgrounds => {
    return (backgrounds || get().backgrounds).filter(background => background.visible).at(-1)?.id;
  }
})));
const ConstellationBackgroundStoreContext = createContext(createConstellationBackgroundStore());
export function ConstellationBackgroundStoreProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const [store] = useState(createConstellationBackgroundStore);
  let t1;
  if ($[0] !== children || $[1] !== store) {
    t1 = <ConstellationBackgroundStoreContext.Provider value={store}>{children}</ConstellationBackgroundStoreContext.Provider>;
    $[0] = children;
    $[1] = store;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function useConstellationBackgroundStore() {
  return useContext(ConstellationBackgroundStoreContext);
}
export function useSelectConstellationBackgroundStore(selector) {
  const store = useConstellationBackgroundStore();
  return useStore(store, selector);
}